import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography, CircularProgress, MobileStepper } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, BrokenImage } from '@mui/icons-material';
import { configurationsService } from '../../../services/configurationsService';

const GalleryWidget: React.FC = () => {
    const [images, setImages] = useState<string[]>([]);
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchImages = async () => {
            try {
                const data = await configurationsService.getGalleryImages();
                if (data.images && data.images.length > 0) {
                    setImages(data.images);
                }
                setLoading(false);
            } catch (err) {
                console.error('Failed to load gallery', err);
                setError('Failed to load gallery');
                setLoading(false);
            }
        };

        fetchImages();
    }, []);

    // Auto-rotation
    useEffect(() => {
        if (images.length <= 1) return;

        const timer = setInterval(() => {
            setActiveStep((prevActiveStep) => (prevActiveStep + 1) % images.length);
        }, 4000);

        return () => clearInterval(timer);
    }, [images]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => (prevActiveStep + 1) % images.length);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => (prevActiveStep - 1 + images.length) % images.length);
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={20} /></Box>;
    }

    if (error) {
        return <Typography color="error" variant="caption">{error}</Typography>;
    }

    if (images.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <BrokenImage sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">No images in gallery</Typography>
            </Box>
        );
    }

    const currentImage = images[activeStep];
    // Assuming backend endpoint lists files, but files are served by frontend public folder
    // The volume mount for backend was just to LIST them.
    // The frontend can access them at /gallery/{filename} because they are in public/gallery.
    const imageUrl = `/gallery/${currentImage}`;

    return (
        <Box sx={{ position: 'relative', width: '100%', height: 350, display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflow: 'hidden' }}>
            <Box
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    width: '100%',
                    height: '100%'
                }}
            >
                <img
                    src={imageUrl}
                    alt={currentImage}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = '';
                    }}
                />

                {images.length > 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                            color: 'white'
                        }}
                    >
                        <MobileStepper
                            steps={images.length}
                            position="static"
                            activeStep={activeStep}
                            nextButton={
                                <IconButton size="small" onClick={handleNext} sx={{ color: 'white' }}>
                                    <KeyboardArrowRight />
                                </IconButton>
                            }
                            backButton={
                                <IconButton size="small" onClick={handleBack} sx={{ color: 'white' }}>
                                    <KeyboardArrowLeft />
                                </IconButton>
                            }
                            sx={{
                                background: 'transparent',
                                justifyContent: 'center',
                                '& .MuiMobileStepper-dot': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.4)'
                                },
                                '& .MuiMobileStepper-dotActive': {
                                    backgroundColor: 'white'
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default GalleryWidget;
